import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, filter, map, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { GetErrorMessageTranslationedValue, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { LogicalOperator } from '@app/shared/enums';
import { OrgUnit, OrgUnitUpdateResponse } from '@app/shared/classes/org-unit';
import { TerminalSearchGridComponent } from '@shared/components/terminal-search-grid/terminal-search-grid.component';
import { Terminal } from '@app/shared/classes/terminal';
import { SubRegion } from '@app/shared/classes/subRegion';
import { Utilities } from '@app/shared/utils/utilities';

@Component({
  selector: 'app-org-unit-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SpinnerButtonComponent,
    TranslateModule,
    NgxsFormPluginModule,
    ReactiveFormsModule,
    MatDialogModule,
    AngularMaterialModule,
    TerminalSearchGridComponent,
  ],
  templateUrl: './org-unit-edit-modal-dialog.component.html',
  styleUrl: './org-unit-edit-modal-dialog.component.css'
})
export class OrgUnitEditModalDialogComponent extends EditModalDialogBasedComponent<OrgUnitEditModalDialogComponent, OrgUnit> {
  terminalAutoCompleteControl = new FormControl('');
  formPath = NgxsFormPath.ORG_UNIT_EDIT_FORM;
  title = "";
  country = "";
  terminalIdFilterSub = new BehaviorSubject<string>('');
  terminalIdFilter$ = this.terminalIdFilterSub.asObservable();

  subRegionsBySelectedCountry$: Observable<SubRegion[]>;
  isSubRegionAvailable = false;

  @Select(AppDataState.tasksExecuting([NgxsActionName.UPDATE_ORG_UNIT], LogicalOperator.OR)) processing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_SUB_REGIONS)) subRegions$: Observable<WebApiResponse<SubRegion[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.UPDATE_ORG_UNIT)) successResponse$: Observable<OrgUnitUpdateResponse>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_ORG_UNIT)) errorResponse$: Observable<WebApiResponseBase>;

  constructor(public dialogRef: MatDialogRef<OrgUnitEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<OrgUnit>) {
    super();
    this.dataInit(dialogRef, data);
    this.isCreate = data.isCreate;
    this.initForm(data);
    this.title = data.isCreate ? "ml_144036" : "ml_143925";
  }

  initForm = (data: InjectDialogData<OrgUnit>) => {
    if (data.isCreate) {
      this.editForm = this.formBuilder.group({
        newOrgUnitMap: true,
        terminalId: ["", Validators.required],
        terminal_id: -1,
        terminalName: ["", Validators.required],
        country: ["", Validators.required],
        subregion_id: [null, Validators.required],
      });
    }
    else {
      this.editForm = this.formBuilder.group({
        newOrgUnitMap: [false],
        terminalId: ["", Validators.required],
        terminal_Id: [data.data.terminal_Id],
        terminalName: [data.data.terminalName],
        country: [data.data.country],
        subRegionName: [data.data.subRegionName, Validators.required],
        subregion_id: [data.data.subregion_id, Validators.required],
      });
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateOrgUnit), withLatestFrom(this.errorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));
  }

  onTerminalIdKeyUp = (event: any) => {
    this.terminalIdFilterSub.next(event.target.value as string);
  }

  onTerminalItemClicked = (event: any) => {
    var currentValue = { terminal_id: -1, terminalName: '', terminalId: '', country: '' };
    if (event.toggled) {
      var terminal = event.item as Terminal;

      currentValue.terminalId = terminal.terminalId;
      currentValue.terminal_id = terminal.terminal_Id;
      currentValue.terminalName = terminal.terminalName;
      currentValue.country = terminal.country;

      this.country = terminal.country;
      this.subRegionsBySelectedCountry$ = this.subRegions$.pipe(
        filter(res => res !== undefined),
        map(res => res.data),
        map(data => data.filter((subRegion) => subRegion.country === terminal.country))
      );

      this.terminalIdFilterSub.next("");
    }

    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateOrgUnit());
  }

  override getErrorMessage = (errorResponse: WebApiResponseBase) => {
    if (!errorResponse) {
      return "";
    }

    if (Utilities.isEmptyString(errorResponse.errorMessage_Translation_Id)) {
      return GetErrorMessageTranslationedValue(errorResponse, this.translateService);
    }

    return this.translateService.instant(errorResponse.errorMessage_Translation_Id).replace('{0}', this.editForm.controls['terminalId'].value);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.terminalIdFilterSub.complete();
  }
}
